import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { quizAPI } from '../services/api';
import { FiLogOut, FiUser, FiBook, FiTrendingUp, FiAward, FiMenu, FiX, FiSettings, FiClock, FiShoppingCart, FiTarget, FiZap, FiCheckCircle, FiChevronRight } from 'react-icons/fi';
import { API_BASE_URL, BASE_URL, institute_id } from '../config/config';
const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [settings, setSettings] = useState({});
  const [stats, setStats] = useState({
    total_quizzes: 0,
    average_score: 0,
    best_score: 0,
    recent_quizzes: []
  });
  const [loading, setLoading] = useState(true);
  const fetchSiteSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/site-settings?institute_id=${institute_id}`);
      const data = await response.json();
      if (data.success) {
        setSettings(data.data);
      }
    } catch (error) {
      console.error('Error fetching site settings:', error);
    }
  };

  useEffect(() => {
    fetchUserStats();
    fetchSiteSettings();
  }, []);

  const fetchUserStats = async () => {
    try {
      const response = await quizAPI.getUserStats(user.id);
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const statsData = [
    { 
      icon: FiBook, 
      label: 'Quizzes Taken', 
      value: stats.total_quizzes, 
      color: 'bg-blue-500',
      textColor: 'text-blue-500'
    },
    { 
      icon: FiTrendingUp, 
      label: 'Average Score', 
      value: `${stats.average_score}%`, 
      color: 'bg-green-500',
      textColor: 'text-green-500'
    },
    { 
      icon: FiAward, 
      label: 'Best Score', 
      value: `${stats.best_score}%`, 
      color: 'bg-purple-500',
      textColor: 'text-purple-500'
    },
  ];

  // Mobile stats data with different styling
  const mobileStatsData = [
    { 
      icon: FiBook, 
      label: 'Tests Taken', 
      value: stats.total_quizzes, 
      gradient: 'from-blue-500 to-blue-600',
      iconBg: 'bg-blue-500/20',
      iconColor: 'text-blue-600'
    },
    { 
      icon: FiTrendingUp, 
      label: 'Avg Score', 
      value: `${stats.average_score}%`, 
      gradient: 'from-green-500 to-emerald-600',
      iconBg: 'bg-green-500/20',
      iconColor: 'text-green-600'
    },
    { 
      icon: FiAward, 
      label: 'Best Score', 
      value: `${stats.best_score}%`, 
      gradient: 'from-purple-500 to-pink-600',
      iconBg: 'bg-purple-500/20',
      iconColor: 'text-purple-600'
    },
  ];

  return (
    <>
      {/* MOBILE VIEW - App Style (Hidden on Desktop) */}
      <div className="md:hidden min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pb-24">
        {loading ? (
          <div className="animate-pulse">
            {/* Hero Skeleton */}
            <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-b-2xl sm:rounded-b-3xl p-4 sm:p-6 pb-6 sm:pb-8">
              <div className="flex items-center justify-between mb-5 sm:mb-6">
                <div className="h-6 sm:h-8 bg-white/30 rounded-lg w-24 sm:w-32"></div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/30 rounded-full"></div>
              </div>
              <div className="h-8 sm:h-10 bg-white/30 rounded-lg w-3/4 mb-2 sm:mb-3"></div>
              <div className="h-5 sm:h-6 bg-white/30 rounded-lg w-1/2"></div>
            </div>
            
            {/* Stats Skeleton */}
            <div className="px-4 sm:px-6 mt-2 mb-6">
              <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 h-20 sm:h-24"></div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Hero Section with Profile */}
            <div className="bg-gradient-to-br from-blue-500 via-blue-600 to-purple-600 rounded-b-2xl sm:rounded-b-3xl shadow-xl relative overflow-hidden">
              {/* Decorative Elements */}
              <div className="absolute top-0 right-0 w-48 sm:w-64 h-48 sm:h-64 bg-white/10 rounded-full -mr-24 sm:-mr-32 -mt-24 sm:-mt-32"></div>
              <div className="absolute bottom-0 left-0 w-32 sm:w-48 h-32 sm:h-48 bg-white/10 rounded-full -ml-16 sm:-ml-24 -mb-16 sm:-mb-24"></div>
              
              <div className="relative px-4 sm:px-6 pt-5 sm:pt-6 pb-8 sm:pb-10">
                {/* Top Bar */}
                <div className="flex items-center justify-between mb-6 sm:mb-8">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    {settings?.logo ? (
                      <img 
                        src={settings.logo}
                        alt={settings.site_name || 'Logo'}
                        className="h-8 sm:h-10 w-auto"
                      />
                    ) : (
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 backdrop-blur-lg rounded-lg sm:rounded-xl flex items-center justify-center">
                        <FiZap className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-lg">
                      {user?.firstname?.[0]}{user?.lastname?.[0] || user?.firstname?.[1]}
                    </div>
                    <button
                      onClick={() => setShowMobileMenu(!showMobileMenu)}
                      className="w-9 h-9 sm:w-10 sm:h-10 bg-white/20 backdrop-blur-lg rounded-full flex items-center justify-center hover:bg-white/30 transition-all active:scale-95"
                    >
                      {showMobileMenu ? (
                        <FiX className="w-5 h-5 text-white" />
                      ) : (
                        <FiMenu className="w-5 h-5 text-white" />
                      )}
                    </button>
                  </div>
                </div>


                {/* Greeting */}
                <div className="mb-2">
                  <p className="text-white/80 text-xs sm:text-sm font-medium mb-1">Welcome back,</p>
                  <h1 className="text-white text-2xl sm:text-3xl font-bold mb-1 sm:mb-2 leading-tight">
                    {user?.firstname}! 👋
                  </h1>
                  <p className="text-white/90 text-sm sm:text-base">Ready to crush your goals today?</p>
                </div>
              </div>
            </div>

            {/* Stats Cards - Overlapping Hero */}
            <div className="px-4 mt-2 mb-6">
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {mobileStatsData.map((stat, index) => (
                  <div 
                    key={index} 
                    className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-4 hover:shadow-xl transition-all active:scale-95 cursor-pointer"
                  >
                    <div className={`${stat.iconBg} w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center mb-2 sm:mb-3`}>
                      <stat.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${stat.iconColor}`} />
                    </div>
                    <p className="text-xl sm:text-2xl font-bold text-gray-800 mb-1 leading-tight">
                      {stat.value}
                    </p>
                    <p className="text-[10px] sm:text-xs text-gray-500 font-medium leading-tight">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Main Content */}
            <div className="px-4 sm:px-6 space-y-5 sm:space-y-6">
              {/* Quick Actions - App Style */}
              <div>
                <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-3 flex items-center">
                  <FiTarget className="mr-2 w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                  Quick Actions
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  <Link 
                    to="/courses" 
                    className="bg-gradient-to-br from-orange-500 to-red-500 rounded-xl sm:rounded-2xl p-4 sm:p-5 shadow-lg hover:shadow-xl transition-all active:scale-95 group min-h-[120px] sm:min-h-[140px]"
                  >
                    <div className="flex flex-col h-full justify-between">
                      <FiShoppingCart className="w-7 h-7 sm:w-8 sm:h-8 text-white mb-2 sm:mb-3" />
                      <div>
                        <h3 className="text-white font-bold text-base sm:text-lg mb-1 leading-tight">Browse</h3>
                        <p className="text-white/80 text-[11px] sm:text-xs leading-tight">Explore courses</p>
                      </div>
                    </div>
                  </Link>

                  <Link 
                    to="/my-courses" 
                    className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl sm:rounded-2xl p-4 sm:p-5 shadow-lg hover:shadow-xl transition-all active:scale-95 group min-h-[120px] sm:min-h-[140px]"
                  >
                    <div className="flex flex-col h-full justify-between">
                      <FiBook className="w-7 h-7 sm:w-8 sm:h-8 text-white mb-2 sm:mb-3" />
                      <div>
                        <h3 className="text-white font-bold text-base sm:text-lg mb-1 leading-tight">My Courses</h3>
                        <p className="text-white/80 text-[11px] sm:text-xs leading-tight">Continue learning</p>
                      </div>
                    </div>
                  </Link>
                </div>
              </div>

              {/* Recent Activity */}
              {/* {stats.recent_quizzes && stats.recent_quizzes.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-base sm:text-lg font-bold text-gray-800 flex items-center">
                      <FiClock className="mr-2 w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                      Recent Activity
                    </h2>
                    <Link to="/history" className="text-xs sm:text-sm text-blue-600 font-semibold flex items-center hover:text-blue-700">
                      View All
                      <FiChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                    </Link>
                  </div>
                  
                  <div className="space-y-2 sm:space-y-3">
                    {stats.recent_quizzes.slice(0, 5).map((quiz, index) => {
                      const isExam = quiz.type === 'exam';
                      const scoreColor = quiz.percentage >= 70 ? 'bg-green-500' : 
                                       quiz.percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500';
                      
                      return (
                        <div 
                          key={index} 
                          className="bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-lg transition-all p-3 sm:p-4 active:scale-98 cursor-pointer"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                              <div className={`w-1 h-12 sm:h-14 ${scoreColor} rounded-full flex-shrink-0`}></div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
                                  <p className="font-bold text-gray-800 truncate text-xs sm:text-sm">
                                    {isExam && quiz.exam_name ? quiz.exam_name : `Practice Quiz ${index + 1}`}
                                  </p>
                                  <span className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-bold flex-shrink-0 ${
                                    isExam 
                                      ? 'bg-purple-100 text-purple-700' 
                                      : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {isExam ? '📝' : '🎯'}
                                  </span>
                                </div>
                                <p className="text-[10px] sm:text-xs text-gray-500">
                                  {new Date(quiz.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </p>
                              </div>
                            </div>
                            
                            <div className="text-right flex-shrink-0 ml-2 sm:ml-3">
                              <p className={`text-lg sm:text-xl font-bold leading-tight ${
                                quiz.percentage >= 70 ? 'text-green-600' : 
                                quiz.percentage >= 50 ? 'text-yellow-600' : 
                                'text-red-600'
                              }`}>
                                {quiz.percentage}%
                              </p>
                              <p className="text-[10px] sm:text-xs text-gray-500 font-medium">
                                {quiz.correct_answers}/{quiz.total_questions}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )} */}

              {/* Empty State for No Activity */}
              {(!stats.recent_quizzes || stats.recent_quizzes.length === 0) && (
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-md p-6 sm:p-8 text-center">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <FiTarget className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600" />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-2">Start Your Journey</h3>
                  <p className="text-gray-600 text-xs sm:text-sm mb-5 sm:mb-6">Take your first test and track your progress</p>
                  <Link 
                    to="/my-courses" 
                    className="inline-flex items-center px-5 py-2.5 sm:px-6 sm:py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold text-sm sm:text-base rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transition-all active:scale-95"
                  >
                    Get Started
                    <FiChevronRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                  </Link>
                </div>
              )}
            </div>
          </>
        )}

        {/* Mobile Menu Modal - Fixed Positioning */}
        {showMobileMenu && (
          <>
            {/* Overlay */}
            <div 
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setShowMobileMenu(false)}
            ></div>
            
            {/* Dropdown Menu */}
            <div className="fixed top-16 right-4 w-72 bg-white rounded-2xl shadow-2xl z-50 overflow-hidden">
              <div className="p-4 bg-gradient-to-br from-blue-500 to-purple-600">
                <div className="flex items-center space-x-3 text-white">
                  <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-lg flex items-center justify-center font-bold text-lg">
                    {user?.firstname?.[0]}{user?.lastname?.[0] || user?.firstname?.[1]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">{user?.firstname} {user?.lastname}</p>
                    <p className="text-xs text-white/80 truncate">{user?.email}</p>
                  </div>
                </div>
              </div>
              
              <div className="p-2">
                <Link
                  to="/profile"
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors active:scale-95"
                >
                  <FiSettings className="w-5 h-5 text-gray-600" />
                  <span className="font-medium text-gray-700">Profile Settings</span>
                </Link>
                
                <button
                  onClick={async () => {
                    setShowMobileMenu(false);
                    await logout();
                    navigate('/login');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-red-50 transition-colors active:scale-95 text-red-600"
                >
                  <FiLogOut className="w-5 h-5" />
                  <span className="font-medium">Logout</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* DESKTOP VIEW - Traditional UI (Hidden on Mobile) */}
      <div className="hidden md:block min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
          
           
            <Link to="/" className="flex items-center space-x-2">
            {settings?.logo ? (
              <>
                <img 
                  src={`${settings.logo}`}
                  alt={settings.site_name || 'Logo'}
                  className="h-10 w-auto"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.querySelector('.fallback-logo').style.display = 'flex';
                  }}
                />
                <div className="fallback-logo w-10 h-10 bg-gradient-to-br from-primary-500 to-purple-600 rounded-lg items-center justify-center" style={{ display: 'none' }}>
                  {/* <span className="text-white font-bold text-xl">R</span> */}
                </div>
              </>
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-purple-600 rounded-lg flex items-center justify-center">
                {/* <span className="text-white font-bold text-xl">R</span> */}
              </div>
            )}
            {/* <div className="hidden sm:block">
              <h1 className="text-xl font-bold text-gray-800">
                {settings?.site_name || 'Raj Economics'}
              </h1>
              <p className="text-xs text-gray-500">
                {settings?.site_tagline || 'RECC'}
              </p>
            </div> */}
          </Link>


            {/* Mobile Menu Button */}
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              {showMenu ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
            </button>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white font-semibold text-lg">
                  {user?.firstname?.[0]}{user?.lastname?.[0] || user?.firstname?.[1]}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700">
                    {user?.firstname} {user?.lastname}
                  </p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
              </div>
              <Link to="/profile" className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <FiSettings className="w-5 h-5 text-gray-600" />
              </Link>
              <button
                onClick={handleLogout}
                className="btn-secondary !py-2 !px-4 text-sm"
              >
                <FiLogOut className="inline mr-2" />
                Logout
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {showMenu && (
            <div className="md:hidden mt-4 pt-4 border-t">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white font-semibold text-lg">
                  {user?.firstname?.[0]}{user?.lastname?.[0] || user?.firstname?.[1]}
                </div>
                <div>
                  <p className="font-semibold text-gray-700">
                    {user?.firstname} {user?.lastname}
                  </p>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                </div>
              </div>
              <div className="space-y-2">
                <Link
                  to="/profile"
                  className="block w-full text-left px-4 py-2 rounded-lg hover:bg-gray-100"
                  onClick={() => setShowMenu(false)}
                >
                  <FiSettings className="inline mr-2" />
                  Profile Settings
                </Link>
                <button
                  onClick={handleLogout}
                  className="btn-secondary w-full text-sm"
                >
                  <FiLogOut className="inline mr-2" />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Welcome Section */}
        <div className="card mb-6 sm:mb-8 bg-gradient-to-r from-primary-500 to-secondary-500 text-white">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-4 md:mb-0">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2">
                Welcome back, {user?.firstname}! 👋
              </h2>
              <p className="text-sm sm:text-base text-white/90">Ready to test your knowledge today?</p>
            </div>
            <Link
              to="/my-courses"
              className="w-full md:w-auto text-center md:mt-0 bg-white text-primary-500 px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              My Courses
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary-500 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading stats...</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8">
              {statsData.map((stat, index) => (
                <div key={index} className="card hover:shadow-xl transition-shadow cursor-pointer">
                  <div className="flex items-center">
                    <div className={`${stat.color} p-3 sm:p-4 rounded-lg mr-3 sm:mr-4`}>
                      <stat.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs sm:text-sm">{stat.label}</p>
                      <p className={`text-xl sm:text-2xl md:text-3xl font-bold ${stat.textColor}`}>
                        {stat.value}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Recent Quizzes */}
            {stats.recent_quizzes && stats.recent_quizzes.length > 0 && (
              <div className="card mb-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Recent Results</h3>
                <div className="space-y-3">
                  {stats.recent_quizzes.map((quiz, index) => {
                    const isExam = quiz.type === 'exam';
                    return (
                      <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex items-center space-x-3 flex-1">
                          <FiClock className="text-gray-400" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-700 truncate">
                                {isExam && quiz.exam_name ? quiz.exam_name : `Practice Quiz ${index + 1}`}
                              </p>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold flex-shrink-0 ${
                                isExam 
                                  ? 'bg-purple-100 text-purple-700' 
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {isExam ? '📝 Test' : '🎯 Quiz'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500">
                              {new Date(quiz.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className={`text-lg font-bold ${
                            quiz.percentage >= 70 ? 'text-green-500' : 
                            quiz.percentage >= 50 ? 'text-yellow-500' : 
                            'text-red-500'
                          }`}>
                            {quiz.percentage}%
                          </p>
                          <p className="text-sm text-gray-500">
                            {quiz.correct_answers}/{quiz.total_questions}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Link 
                  to="/history" 
                  className="block text-center text-primary-500 hover:text-primary-600 font-medium mt-4"
                >
                  View All History →
                </Link>
              </div>
            )}
          </>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <Link to="/courses" className="card hover:shadow-xl transition-all group cursor-pointer">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0 mr-3">
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2 group-hover:text-primary-500 transition-colors">
                  Browse Courses
                </h3>
                <p className="text-sm sm:text-base text-gray-600">Explore and purchase new courses</p>
              </div>
              <FiShoppingCart className="w-10 h-10 sm:w-12 sm:h-12 text-orange-500 group-hover:scale-110 transition-transform flex-shrink-0" />
            </div>
          </Link>

          <Link to="/my-courses" className="card hover:shadow-xl transition-all group cursor-pointer">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0 mr-3">
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2 group-hover:text-primary-500 transition-colors">
                  My Courses
                </h3>
                <p className="text-sm sm:text-base text-gray-600">Access your purchased courses and test series</p>
              </div>
              <FiBook className="w-10 h-10 sm:w-12 sm:h-12 text-primary-500 group-hover:scale-110 transition-transform flex-shrink-0" />
            </div>
          </Link>

          <Link to="/history" className="card hover:shadow-xl transition-all group cursor-pointer">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0 mr-3">
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2 group-hover:text-primary-500 transition-colors">
                  Quiz History
                </h3>
                <p className="text-sm sm:text-base text-gray-600">View your past quiz results</p>
              </div>
              <FiTrendingUp className="w-10 h-10 sm:w-12 sm:h-12 text-green-500 group-hover:scale-110 transition-transform flex-shrink-0" />
            </div>
          </Link>
        </div>
      </main>
      </div>
    </>
  );
};

export default Dashboard;

